// THEME CONFIGURATION
// ----------------
// Professional color palette based on PMS colors
export const THEME_COLORS = {
  // Primary color palette - Professional brand colors
  primary: {
    dark: "#2D2926",      // PMS 143 Dark - Deep brown/charcoal
    light: "#F1B434",     // PMS 143 Light - Professional gold
    gray: "#888B8D",      // PMS Cool Gray 8 - Professional gray
  },
  // Extended palette with whites and off-whites
  neutral: {
    black: "#1A1A1A",      // Rich black
    darkBrown: "#2D2926",  // Primary dark color
    charcoal: "#3A3633",   // Slightly lighter than primary dark
    mediumGray: "#888B8D", // Primary gray
    lightGray: "#B8BABBC", // Lighter variant of primary gray
    white: "#FFFFFF",      // Pure white
    offWhite: "#FEFEFE",   // Slightly off white
    cream: "#FBF9F6",      // Warm cream complement
    lightCream: "#FDFCFA", // Very light cream
    warmWhite: "#FAF8F5",  // Warm white to complement browns
  },
  // Gold variants for different uses
  gold: {
    primary: "#F1B434",    // Main brand gold
    light: "#F5C858",      // Lighter variant
    lighter: "#F8D982",    // Even lighter
    lightest: "#FBE6AC",   // Very light gold tint
    dark: "#D19F2E",       // Darker variant
    darker: "#B8912A",     // Even darker
  },
  // Semantic colors maintaining brand harmony
  semantic: {
    error: "#D32F2F",      // Professional red
    warning: "#F57C00",    // Professional orange
    success: "#388E3C",    // Professional green
    info: "#1976D2",       // Professional blue
  }
};

// Gradient definitions using professional color palette
export const GRADIENTS = {
  // Primary gradients
  primaryGold: `linear-gradient(135deg, ${THEME_COLORS.gold.primary} 0%, ${THEME_COLORS.gold.dark} 100%)`,
  subtleGold: `linear-gradient(135deg, ${THEME_COLORS.gold.lightest} 0%, ${THEME_COLORS.gold.light} 100%)`,
  goldAccent: `linear-gradient(90deg, ${THEME_COLORS.gold.primary} 0%, ${THEME_COLORS.gold.darker} 100%)`,
  
  // Light gradients for backgrounds
  lightPrimary: `linear-gradient(135deg, ${THEME_COLORS.neutral.lightCream} 0%, ${THEME_COLORS.neutral.cream} 100%)`,
  lightSubtle: `linear-gradient(90deg, ${THEME_COLORS.neutral.white} 0%, ${THEME_COLORS.neutral.warmWhite} 100%)`,
  
  // Hero section gradients
  heroBackground: `linear-gradient(135deg, ${THEME_COLORS.neutral.warmWhite} 0%, ${THEME_COLORS.neutral.cream} 100%)`,
  heroAccent: `linear-gradient(135deg, ${THEME_COLORS.gold.lightest} 0%, ${THEME_COLORS.neutral.warmWhite} 100%)`,
  
  // Text gradients for professional appearance
  textGold: `linear-gradient(90deg, ${THEME_COLORS.primary.dark} 0%, ${THEME_COLORS.gold.primary} 100%)`,
  textPrimary: `linear-gradient(90deg, ${THEME_COLORS.primary.dark} 30%, ${THEME_COLORS.gold.primary} 100%)`,
  
  // Dark theme gradients
  darkPrimary: `linear-gradient(135deg, ${THEME_COLORS.primary.dark} 0%, ${THEME_COLORS.neutral.charcoal} 100%)`,
  darkAccent: `linear-gradient(90deg, ${THEME_COLORS.primary.dark} 0%, ${THEME_COLORS.primary.gray} 100%)`,
};

// Chart colors using the professional palette
export const CHART_COLORS = [
  THEME_COLORS.gold.primary,     // Main gold
  THEME_COLORS.gold.dark,        // Darker gold
  THEME_COLORS.gold.light,       // Lighter gold
  THEME_COLORS.primary.gray,     // Primary gray
  THEME_COLORS.neutral.mediumGray, // Medium gray
  THEME_COLORS.neutral.lightGray,  // Light gray
  THEME_COLORS.primary.dark,     // Dark brown
  THEME_COLORS.neutral.charcoal, // Charcoal
  THEME_COLORS.gold.lighter,     // Very light gold
  THEME_COLORS.gold.darker,      // Very dark gold
  THEME_COLORS.neutral.warmWhite, // Warm white
  THEME_COLORS.neutral.cream,    // Cream
];

// Professional chart theme
export const PROFESSIONAL_CHART_COLORS = {
  PRIMARY: THEME_COLORS.gold.primary,
  SECONDARY: THEME_COLORS.primary.gray,
  ACCENT: THEME_COLORS.gold.dark,
  ERROR: THEME_COLORS.semantic.error,
  WARNING: THEME_COLORS.semantic.warning,
  INFO: THEME_COLORS.semantic.info,
  SUCCESS: THEME_COLORS.semantic.success,
  BACKGROUND: THEME_COLORS.neutral.white,
  SURFACE: THEME_COLORS.neutral.warmWhite,
  TEXT: THEME_COLORS.primary.dark,
  MUTED: THEME_COLORS.primary.gray
};

// Backward compatibility alias
export const DARK_CHART_COLORS = PROFESSIONAL_CHART_COLORS;

// Legacy gold theme object - maintaining for backward compatibility
export const GOLD_THEME = {
  GOLD_BRIGHT: THEME_COLORS.gold.primary,
  GOLD_RICH: THEME_COLORS.gold.primary,
  GOLD_DEEP: THEME_COLORS.gold.dark,
  GOLD_LIGHT: THEME_COLORS.gold.light,
  BLACK: THEME_COLORS.neutral.black,
  DARK_GRAY: THEME_COLORS.neutral.charcoal,
  MEDIUM_GRAY: THEME_COLORS.neutral.mediumGray,
  LIGHT_GRAY: THEME_COLORS.neutral.lightGray,
  WHITE: THEME_COLORS.neutral.white,
  OFF_WHITE: THEME_COLORS.neutral.offWhite
};

// Common chart dimensions
export const CHART_DIMENSIONS = {
  SMALL: { width: 400, height: 300 },
  MEDIUM: { width: 600, height: 400 },
  LARGE: { width: 900, height: 400 },
  MAP: { width: 900, height: 500 }
};

// Error messages
export const ERROR_MESSAGES = {
  FETCH_FAILED: 'Failed to load data. Please try again later.',
  NO_DATA: 'No data available.',
  NETWORK_ERROR: 'Network error. Please check your connection.'
};

// Loading states
export const LOADING_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error'
};
